import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';

import { Box, CircularProgress } from '@mui/joy';
import { Text } from '../Typography';
import { Icon } from '../../assets';
import { default as ReactApexChart } from 'react-apexcharts'
import { useDataProvider } from '../../../mtska-frontend-data-provider';
import { useTranslation } from 'react-i18next';
import {
    buildPieChartConfig,
    getStyles,
    isLegendAtBottom,
    PRINT_PIE_CHART_HEIGHT,
    resolvePieLayout,
    translatePieLabel,
} from './pie.functions';
import { useDashboardPrintRender } from 'src/mtska-frontend-modules/modules/_shared/dashboardConfigured/DashboardPrintRenderContext';

/** Deve essere riferimento stabile: `monochrome = { enabled: false }` nel destructuring ricrea un oggetto a ogni render. */
const PIE_MONOCHROME_DEFAULT = { enabled: false };

const comparableFiltersKey = (filtersProp) => {
    const items = filtersProp?.items;
    if (!Array.isArray(items)) return '';
    return JSON.stringify(
        items.map(({ field, operator, value }) => ({ field, operator, value })),
    );
};

const resolveContainerWidth = (width, containerWidth) => {
    if (typeof width === 'number') {
        return width;
    }
    if (typeof width === 'string' && width.includes('%')) {
        const percent = parseFloat(width) / 100;
        return Math.floor(containerWidth * percent);
    }
    return containerWidth;
};

const PieLegendBottom = ({ labels, colors, height, t, onSelect, hasData }) => (
    <div
        className="mtska-pie-legend-bottom"
        style={{
            height,
            minHeight: height,
            maxHeight: height,
            overflowY: 'auto',
            overflowX: 'hidden',
            boxSizing: 'border-box',
        }}
    >
        {labels.map((label, index) => {
            const marker = (
                <span
                    className="mtska-pie-legend-marker"
                    style={{ backgroundColor: colors?.[index] || '#cccccc' }}
                />
            );
            const text = (
                <span style={{ fontSize: 11, lineHeight: 1.35, textAlign: 'left' }}>
                    {translatePieLabel(t, label)}
                </span>
            );

            if (onSelect && hasData) {
                return (
                    <button
                        key={`${label}-${index}`}
                        type="button"
                        className="mtska-pie-legend-item"
                        onClick={() => onSelect(label)}
                        style={{ cursor: 'pointer' }}
                    >
                        {marker}
                        {text}
                    </button>
                );
            }

            return (
                <span key={`${label}-${index}`} className="mtska-pie-legend-item">
                    {marker}
                    {text}
                </span>
            );
        })}
    </div>
);

const Pie = ({ title, dataProviderName, icon, filters, handleFilterChange, mode = 'light', palette = 'palette1', monochrome = PIE_MONOCHROME_DEFAULT, labelToColorMap, width, legendWidth, donutWidth, height = 230, showTotal = true, showName = true, showValues = true, showLegend = true, format, legendPosition = 'right', showSliceTooltip = true }) => {
    const { items, loadAll, loading, reloadCounter } = useDataProvider(dataProviderName);
    const { t, i18n } = useTranslation();
    const printMode = useDashboardPrintRender();

    const effectiveLegendPosition = printMode ? 'bottom' : legendPosition;
    const useExternalLegend = showLegend && isLegendAtBottom(effectiveLegendPosition);

    /** Stabile sul contenuto: `ConfiguredDashboardPage` ricrea spesso `filters` con nuovo riferimento ma stessi field/operator/value. */
    const filtersLogicalKey = comparableFiltersKey(filters);

    useEffect(() => {
        if (!dataProviderName) return;
        loadAll(filters);
    }, [reloadCounter, dataProviderName, filtersLogicalKey]);

    const chartWrapperRef = useRef(null);
    const resizeDebounceRef = useRef(null);
    const [measuredWidth, setMeasuredWidth] = useState(400);

    useEffect(() => {
        if (!chartWrapperRef.current) return;

        const resizeObserver = new ResizeObserver((entries) => {
            if (resizeDebounceRef.current) clearTimeout(resizeDebounceRef.current);
            resizeDebounceRef.current = setTimeout(() => {
                for (const entry of entries) {
                    setMeasuredWidth(entry.contentRect.width);
                }
            }, 120);
        });

        resizeObserver.observe(chartWrapperRef.current);
        return () => {
            if (resizeDebounceRef.current) clearTimeout(resizeDebounceRef.current);
            resizeObserver.disconnect();
        };
    }, []);

    const containerWidth = resolveContainerWidth(width, measuredWidth);
    const containerHeight = printMode ? PRINT_PIE_CHART_HEIGHT : height;

    const layout = useMemo(
        () =>
            resolvePieLayout({
                containerWidth,
                containerHeight,
                legendPosition,
                legendWidth,
                showLegend,
                printMode,
            }),
        [containerWidth, containerHeight, legendPosition, legendWidth, showLegend, printMode],
    );

    const itemsDataKey = useMemo(() => {
        if (!items || typeof items !== 'object' || Array.isArray(items)) return '';
        const lbl = Array.isArray(items.labels) ? items.labels : [];
        const s = Array.isArray(items.series) ? items.series : [];
        return `${lbl.join('\0')}\0${s.join(',')}`;
    }, [items]);

    const itemsRef = useRef(items);
    itemsRef.current = items;

    const handleFilterChangeRef = useRef(handleFilterChange);
    handleFilterChangeRef.current = handleFilterChange;
    const onSliceFilter = useCallback((label) => {
        handleFilterChangeRef.current?.(label);
    }, []);

    const pieConfig = useMemo(
        () =>
            buildPieChartConfig(
                itemsRef.current,
                monochrome,
                showLegend,
                layout,
                mode,
                palette,
                showTotal,
                showName,
                showValues,
                labelToColorMap,
                t,
                onSliceFilter,
                format,
                i18n.resolvedLanguage,
                showSliceTooltip,
                printMode
            ),
        [
            itemsDataKey,
            monochrome,
            showLegend,
            layout,
            mode,
            palette,
            showTotal,
            showName,
            showValues,
            labelToColorMap,
            t,
            onSliceFilter,
            format,
            i18n.resolvedLanguage,
            showSliceTooltip,
            printMode,
        ]
    );

    const series = pieConfig?.series;
    const options = pieConfig?.options;
    const chartHeight = pieConfig?.chartHeight ?? layout.apexHeight;
    const sliceColors = pieConfig?.sliceColors;
    const isLoadingState = loading || !series || !options;

    const hasData = Array.isArray(items?.series) && items.series.reduce((a, b) => a + b, 0) > 0;
    const legendLabels = hasData ? items.labels : [t('Nessun dato')];

    return (
        <Box
            className={`mtska-chart mtska-chart-pie${printMode ? ' mtska-chart-pie--print' : ''}`}
            sx={printMode ? { width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' } : undefined}
        >
            <Icon icon={icon} family="fal"></Icon>
            <Text level="title-sm" className="title">{t(title)}</Text>
            <Box
                className="mtska-chart-pie-body"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    height: containerHeight,
                    minHeight: containerHeight,
                    boxSizing: 'border-box',
                }}
            >
                <Box
                    ref={chartWrapperRef}
                    className="chartwrapper"
                    sx={{
                        ...getStyles(layout, showLegend, useExternalLegend),
                        flex: useExternalLegend ? `0 0 ${layout.graphHeight}px` : undefined,
                        height: useExternalLegend ? layout.graphHeight : containerHeight,
                        minHeight: useExternalLegend ? layout.graphHeight : containerHeight,
                        ...(isLoadingState
                            ? {
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }
                            : {}),
                    }}
                >
                    {isLoadingState ? (
                        <CircularProgress />
                    ) : (
                        <ReactApexChart
                            type="donut"
                            options={options}
                            series={series}
                            height={chartHeight}
                            width={printMode ? '100%' : layout.apexWidth}
                        />
                    )}
                </Box>
                {useExternalLegend && !isLoadingState ? (
                    <PieLegendBottom
                        labels={legendLabels}
                        colors={sliceColors}
                        height={layout.legendHeight}
                        t={t}
                        onSelect={handleFilterChange ? onSliceFilter : undefined}
                        hasData={hasData}
                    />
                ) : null}
            </Box>
        </Box>
    );
}

export default Pie;
